import React, { useState, useEffect } from 'react';
import ScenarioCreationWizard from './ScenarioCreationWizard';
import ScenarioComparison from './ScenarioComparison';
import WhatIfAnalysis from './WhatIfAnalysis';
import { SOPService } from '../../services/sopApi';
import { Scenario, ScenarioRequest } from '../../types/sop';
import LoadingIndicator from '../common/LoadingIndicator';
import ErrorDisplay from '../common/ErrorDisplay';
import './ScenarioPlanning.css';

type ScenarioView = 'list' | 'create' | 'compare' | 'whatif';

const ScenarioPlanning: React.FC = () => {
  const [activeView, setActiveView] = useState<ScenarioView>('list');
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [selectedScenarios, setSelectedScenarios] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadScenarios();
  }, []);

  const loadScenarios = async () => {
    try {
      setLoading(true);
      setError(null);
      const scenarioData = await SOPService.getScenarios();
      setScenarios(scenarioData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load scenarios');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateScenario = async (request: ScenarioRequest) => {
    try {
      const newScenario = await SOPService.createScenario(request);
      setScenarios(prev => [...prev, newScenario]);
      setActiveView('list');
    } catch (err) {
      throw err; // Let the wizard handle the error
    }
  };

  const handleScenarioSelection = (scenarioId: string, selected: boolean) => {
    setSelectedScenarios(prev => {
      if (selected) {
        return [...prev, scenarioId];
      } else {
        return prev.filter(id => id !== scenarioId);
      }
    });
  };

  const handleDeleteScenario = async (scenarioId: string) => {
    if (!window.confirm('Are you sure you want to delete this scenario?')) {
      return;
    }

    try {
      // Note: Delete API would be implemented in SOPService
      // await SOPService.deleteScenario(scenarioId);
      setScenarios(prev => prev.filter(s => s.scenario_id !== scenarioId));
      setSelectedScenarios(prev => prev.filter(id => id !== scenarioId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete scenario');
    }
  };

  const renderScenarioList = () => {
    if (loading) {
      return <LoadingIndicator message="Loading scenarios..." />;
    }

    if (error) {
      return (
        <ErrorDisplay 
          error={error} 
          onRetry={loadScenarios}
          operation="Scenario Planning"
        />
      );
    }

    return (
      <div className="scenario-list">
        <div className="scenario-list-header">
          <h3>Your Scenarios</h3>
          <div className="scenario-actions">
            <button 
              className="btn btn-primary"
              onClick={() => setActiveView('create')}
            >
              Create New Scenario
            </button>
            {selectedScenarios.length >= 2 && (
              <button 
                className="btn btn-secondary"
                onClick={() => setActiveView('compare')}
              >
                Compare Selected ({selectedScenarios.length})
              </button>
            )}
            {selectedScenarios.length === 1 && (
              <button 
                className="btn btn-secondary"
                onClick={() => setActiveView('whatif')}
              >
                What-If Analysis
              </button>
            )}
          </div>
        </div>

        {scenarios.length === 0 ? (
          <div className="empty-scenarios">
            <h4>No scenarios yet</h4>
            <p>Create your first scenario to start exploring different planning options.</p>
            <button 
              className="btn btn-primary"
              onClick={() => setActiveView('create')}
            >
              Create First Scenario
            </button>
          </div>
        ) : (
          <div className="scenarios-grid">
            {scenarios.map(scenario => (
              <div 
                key={scenario.scenario_id} 
                className={`scenario-card ${selectedScenarios.includes(scenario.scenario_id) ? 'selected' : ''}`}
              >
                <div className="scenario-card-header">
                  <div className="scenario-selection">
                    <input
                      type="checkbox"
                      checked={selectedScenarios.includes(scenario.scenario_id)}
                      onChange={(e) => handleScenarioSelection(scenario.scenario_id, e.target.checked)}
                      className="scenario-checkbox"
                    />
                  </div>
                  <div className="scenario-info">
                    <h4>{scenario.scenario_name}</h4>
                    <div className="scenario-meta">
                      <span className={`scenario-status ${scenario.status}`}>
                        {scenario.status}
                      </span>
                      {scenario.ai_generated && (
                        <span className="ai-badge">AI Generated</span>
                      )}
                    </div>
                  </div>
                  <div className="scenario-actions-menu">
                    <button 
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteScenario(scenario.scenario_id)}
                      title="Delete scenario"
                    >
                      ×
                    </button>
                  </div>
                </div>

                <div className="scenario-card-body">
                  <div className="scenario-assumptions">
                    <h5>Key Assumptions:</h5>
                    <ul>
                      {Object.entries(scenario.assumptions).slice(0, 3).map(([key, value]) => (
                        <li key={key}>
                          <strong>{key.replace(/_/g, ' ')}:</strong> {String(value)}
                        </li>
                      ))}
                      {Object.keys(scenario.assumptions).length > 3 && (
                        <li>... and {Object.keys(scenario.assumptions).length - 3} more</li>
                      )}
                    </ul>
                  </div>

                  <div className="scenario-stats">
                    <div className="stat">
                      <span className="stat-label">Planning Points:</span>
                      <span className="stat-value">{scenario.planning_data?.length || 0}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Created:</span>
                      <span className="stat-value">
                        {new Date(scenario.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'create':
        return (
          <ScenarioCreationWizard 
            onScenarioCreate={handleCreateScenario}
            onCancel={() => setActiveView('list')}
            existingScenarios={scenarios}
          />
        );
      case 'compare':
        return (
          <ScenarioComparison 
            scenarioIds={selectedScenarios}
            scenarios={scenarios}
            onBack={() => setActiveView('list')}
          />
        );
      case 'whatif':
        return (
          <WhatIfAnalysis 
            scenarioId={selectedScenarios[0]}
            scenario={scenarios.find(s => s.scenario_id === selectedScenarios[0])}
            onBack={() => setActiveView('list')}
          />
        );
      default:
        return renderScenarioList();
    }
  };

  return (
    <div className="scenario-planning">
      <div className="scenario-planning-header">
        <h2>Scenario Planning</h2>
        <p>Create, compare, and analyze different planning scenarios with AI-powered insights</p>
        
        {activeView !== 'list' && (
          <button 
            className="btn btn-secondary"
            onClick={() => {
              setActiveView('list');
              setSelectedScenarios([]);
            }}
          >
            ← Back to Scenarios
          </button>
        )}
      </div>

      <div className="scenario-planning-content">
        {renderContent()}
      </div>
    </div>
  );
};

export default ScenarioPlanning;